import { withRouter }                             from 'react-router';
import { connect }                                from 'react-redux';
import * as PropTypes                             from 'prop-types';
import {
    compose,
    lifecycle,
    withState,
    withHandlers,
    getContext,
    branch,
    renderNothing
}                                                 from 'recompose';
import {
    updateCustomField,
    selectCustomField,
    validateCustomFields,
    validateProfanityField,
}                                                 from '../CustomFields/CustomFieldsActions';
import { CustomFieldsState }                      from '../../reduxTypes';
import { MessageTemplate }                        from '../MessageTemplatesList/MessageTemplatesListTypes';
import {
    createMessagePayload,
    sendMessage
}                                                 from '../MessagesFeed/MessagesActions';
import { deactivatePlaceholder }                  from '../ConversationsList/ConversationsListActions';
import { updateRecipientStatus }                  from '../Recipient/RecipientActions';
import { getFilteredFields }                      from '../CustomFields/CustomFieldsSelectors';
import { findIndexByAttributeValue }              from '../../helpers/arrayUtils';
import { checkProfanity }                         from '../../helpers/fetchTools';
import {
    MessageTemplateComponentContainerProps,
    TemplateEditConfig
}                                                 from './MessageTemplateTypes';
import { ViewContextTypes }                       from '../MessageTemplatesList/MessageTemplatesListTypes';

// components
import MessageTemplateComponent                   from './MessageTemplateComponent';

/* Functionality */

const mapStateToProps = (store) => ({
    customFields: getFilteredFields(store),
    activeLanguage: store.languageStore.activeLanguage
});

const mapDispatchers = (dispatch, ownProps: MessageTemplateComponentContainerProps) => ({
    async updateFields(messageTemplate: MessageTemplate, fieldKey: string, value, focusChange?: boolean) {
        // TODO: Someday we should debounce this so that we don't
        // spam the store with too many actions when users are typing
        await dispatch(updateCustomField(fieldKey, value, focusChange));
        checkProfanity(value).then(res => {
            dispatch(validateProfanityField(res.data.containsProfanity, fieldKey));
            return dispatch(validateCustomFields(messageTemplate));
        }).catch( err => {
            console.error(err);
            // In case profanity service is down we want to allow sending all messages... someomne will be offended but thats life.
            dispatch(validateProfanityField(false, fieldKey));
            return dispatch(validateCustomFields(messageTemplate));
        });
    },
    validateProfanity(hasProfanity: boolean, fieldKey: string) {
        dispatch(validateProfanityField(hasProfanity, fieldKey));
    },
    validateFields() {
        dispatch(validateCustomFields(ownProps.messageTemplate));
    },
    selectCF(fieldKey: string, templateId: string) {
        dispatch(selectCustomField(fieldKey, templateId));
    },
    handleSendMessage(conversationId: number, message: string, activeLanguage: string, recipient?: any, templateId?: string) {
        dispatch(sendMessage(conversationId, message, activeLanguage, recipient, templateId));
        dispatch(updateRecipientStatus(conversationId));
    },
    resetPlaceholder() {
        dispatch(deactivatePlaceholder());
    },
});

const sendMessageTemplate = (messageTemplate: MessageTemplate, customFields: CustomFieldsState, conversationId: number, handleSendMessage, activeLanguage: string, recipient, setInputView?) => {
    const editedTemplate = messageTemplate.template.message.split(/\{\{\s*|\s*\}\}/).reduce((str, index) => {
        const currentField = customFields.customFields[index];
        return str + (currentField ? currentField.editedValue : index);
    }, '');
    const message = createMessagePayload(editedTemplate, messageTemplate.id);

    setInputView && setInputView(ViewContextTypes.DEFAULT); // reset input to the default
    handleSendMessage(conversationId, message.content, activeLanguage, recipient, message.templateId);
};

/* Recompose Export */
const MessageTemplateContainer = compose(
    withRouter,
    connect(mapStateToProps, mapDispatchers),
    getContext({
        session: PropTypes.any.isRequired,
        translate: PropTypes.func.isRequired
    }),
    withState('sendInProgress', 'updateSendInProgress', false),
    withHandlers<MessageTemplateComponentContainerProps, {}>({
        handleSelectTemplate: ({ selectMessageTemplate, selectCF }) => (event) => (id: string) => {
            selectMessageTemplate(id);
            if (event.target && event.target.classList.contains('messageTemplate__contentEditable')) {
                selectCF(event.target.dataset.fieldname, id);
            }
        },
        handleCancel: ({ selectMessageTemplate, selectCF }) => () => {
            selectMessageTemplate();
            selectCF(null, null);
        },
        handleEdit: ({ messageTemplate, updateFields }) => (config: TemplateEditConfig) => {
            updateFields(messageTemplate, config.fieldKey, config.value, config.focusChange);
        },
        incrementEditField: ({ messageTemplate, customFields, selectCF }) => () => {
            const currentFieldIndex = findIndexByAttributeValue(messageTemplate.template.fields, 'name', customFields.selectedField);

            if (typeof currentFieldIndex === 'undefined') {
                return selectCF(messageTemplate.template.fields[0].name, messageTemplate.id);
            }

            const nextField = messageTemplate.template.fields[currentFieldIndex + 1].name;
            selectCF(nextField, messageTemplate.id);
        },
        decrementEditField: ({ messageTemplate, customFields, selectCF }) => () => {
            const currentFieldIndex = findIndexByAttributeValue(messageTemplate.template.fields, 'name', customFields.selectedField);

            if (currentFieldIndex === 0) {
                return selectCF(null, null);
            }

            const prevField = messageTemplate.template.fields[currentFieldIndex - 1].name;
            selectCF(prevField, messageTemplate.id);
        },
        handleSend: ({ messageTemplate, customFields, updateSendInProgress, selectCF, match, handleSendMessage, setInputView, activeLanguage, recipient, resetPlaceholder }) => () => {
            resetPlaceholder();
            updateSendInProgress(true);
            selectCF(null, null);
            sendMessageTemplate(messageTemplate, customFields, parseInt(match.params.conversationId, 10), handleSendMessage, activeLanguage, recipient, setInputView);
        }
    }),
    lifecycle<MessageTemplateComponentContainerProps, {}>({
        componentWillUpdate(nextProps) {
            if (
                this.props.activeMessageTemplate !== nextProps.activeMessageTemplate && // The user is changing message templates
                this.props.messageTemplate.id === nextProps.activeMessageTemplate       // The current component is selected
            ) {
                this.props.validateFields();
            }
        }
    }),
    branch(({ activeMessageTemplate, messageTemplate }) => (activeMessageTemplate && activeMessageTemplate !== messageTemplate.id), renderNothing)
);

export default MessageTemplateContainer(MessageTemplateComponent) as React.ComponentClass<MessageTemplateComponentContainerProps>;



// WEBPACK FOOTER //
// ./src/sections/MessageTemplate/index.ts